import { useState } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { ChevronDown, ChevronUp, Connect } from '@carbon/react/icons';
import { CompactField } from 'components/field';
import { FormattedCount } from 'components/formatted-count';
import { formatDecimalFull } from 'lib/ui';
import { formatNumber } from 'utils/number';
import { SubtractDetailsResponse, TractDetailsResponse } from '../types';
import { EMPTY_VALUE, renderLink } from './utils';
import styles from './summary-top-section.module.scss';

/**
 * Display branch for the "Insights Entity" header inside the Entity
 * column. Mirrors `tractSummaryDataAtom`'s union return so callers
 * can pass it through unchanged.
 */
export type InsightsEntityDisplay =
  | { type: 'linkButton'; label: string }
  | { type: 'multipleEntities'; count: number }
  | { type: 'singleEntity'; entity?: { name: string; href: string } };

/** Minimal entity shape for the multi-entity expanded list. */
export interface SummaryEntity {
  id: string | number;
  name: string;
  href?: string | null;
  type?: string | null;
  address?: string | null;
}

/** Subset of `tractSummaryDataAtom`'s output that drives the Entity column. */
export interface SummaryEntityData {
  entity?: {
    name?: string | null;
    address?: string | null;
    type?: string | null;
  } | null;
  account?: {
    name?: string;
    salesforceHref?: string | null;
    billingAddress?: string | null;
    type?: string | null;
  } | null;
  entities: SummaryEntity[];
  insightsEntityDisplay: InsightsEntityDisplay;
  showEntityDetails: boolean;
  hasMultipleEntities: boolean;
}

interface SummaryTopSectionProps {
  /** Tract details payload — feeds the left column AND the Subtract column's "Survey Area" (which inherits from the parent tract). `null` while loading. */
  tract: TractDetailsResponse | null;
  /** Subtract details payload — feeds the middle column. `null` while loading. */
  subtract: SubtractDetailsResponse | null;
  /** Entity / CRM-account derivation from `tractSummaryDataAtom`. */
  summaryData: SummaryEntityData;
  /**
   * Optional callback for the "Link Insights Entity" button when
   * `summaryData.insightsEntityDisplay.type === 'linkButton'`. Wizard
   * consumer typically wires this to `goToStep(1)`.
   */
  onLinkInsightsEntity?: () => void;
  /** Disables the link-entity button when the user lacks CRM-create permission. */
  disableLinkInsightsEntity?: boolean;
}

/**
 * Coerces a boolean to "Yes" / "No" / EMPTY_VALUE. Treats `null` and
 * `undefined` identically — both surface as missing data, not `"No"`.
 *
 * @param {boolean | null | undefined} value - Boolean to format
 * @return {string} "Yes", "No", or em-dash
 */
const formatBoolean = (value: boolean | null | undefined): string => {
  if (value === null || value === undefined) return EMPTY_VALUE;
  return value ? 'Yes' : 'No';
};

/**
 * Formats a depth value (Start / End) with the project's standard
 * decimal formatter (full precision per legacy `tract-details.tsx`).
 *
 * @param {number | null | undefined} value - Depth in feet
 * @return {string} Formatted depth or em-dash
 */
const formatDepth = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return EMPTY_VALUE;
  return formatDecimalFull(value);
};

/**
 * Formats an acreage value with up to 8 decimals + commas. Matches the
 * legacy `tract-details.tsx` precedent and keeps display consistent
 * with the slot-allocations table's acres columns.
 *
 * @param {number | null | undefined} value - Acreage value
 * @return {string} Formatted acreage or em-dash
 */
const formatAcres = (value: number | null | undefined): string =>
  formatNumber({
    value: value ?? undefined,
    minPrecision: 0,
    maxPrecision: 8,
    includeCommas: true,
    fallback: EMPTY_VALUE,
  });

/**
 * Tract Details column — read-only `<CompactField>` rows. Survey Area
 * reads `tract.referenceLocation.name` (the parent tract's reference
 * location, set on the Tract Creation step).
 *
 * @param {object} props - The component props.
 * @return {JSX.Element} The column.
 */
const TractDetailsColumn = ({
  tract,
}: {
  tract: TractDetailsResponse | null;
}): JSX.Element => (
  <div className={styles.column}>
    <span className={styles.columnTitle}>Tract Details</span>
    <div className={styles.fieldsGrid}>
      <CompactField label="Tract Name:" value={tract?.name ?? EMPTY_VALUE} />
      <CompactField
        label="Instrument Type:"
        value={tract?.assetType ?? EMPTY_VALUE}
      />
      <CompactField
        label="Survey Area:"
        value={tract?.referenceLocation?.name ?? EMPTY_VALUE}
      />
      <CompactField
        label="Tract Source:"
        value={tract?.tractSource ?? EMPTY_VALUE}
      />
      <CompactField
        label="Quarter Call:"
        value={tract?.quarterCall ?? EMPTY_VALUE}
      />
      <CompactField
        label="Start Depth:"
        value={formatDepth(tract?.startDepth)}
      />
      <CompactField label="Lots:" value={tract?.lots ?? EMPTY_VALUE} />
      <CompactField label="End Depth:" value={formatDepth(tract?.endDepth)} />
      <CompactField
        label="Grid Exception:"
        value={formatBoolean(tract?.gridException)}
      />
      <CompactField
        label="Alt Tract ID:"
        value={tract?.altAssetId ?? EMPTY_VALUE}
      />
      <CompactField label="Notes:" value={tract?.notes ?? EMPTY_VALUE} />
      <CompactField
        label="Net Acreage Units:"
        value={tract?.netAcreageUnits ?? EMPTY_VALUE}
      />
      <CompactField
        label="Tract Net Acres:"
        value={formatAcres(tract?.tractNetAcres)}
      />
      <CompactField
        label="Tract Gross Acres:"
        value={formatAcres(tract?.grossAcres)}
      />
    </div>
  </div>
);

/**
 * Subtract Details column — read-only `<CompactField>` rows. Survey
 * Area inherits from the parent tract (`tract.referenceLocation.name`)
 * since `SubtractDetailsResponse` doesn't carry it directly. Royalty
 * fields (Lease Royalty Rate / Type / Royalty Burdens / ORRI Burdens)
 * aren't on `SubtractDetailsResponse` today and render as em-dash;
 * will surface once the BE shape is enriched.
 *
 * @param {object} props - The component props.
 * @return {JSX.Element} The column.
 */
const SubtractDetailsColumn = ({
  tract,
  subtract,
}: {
  tract: TractDetailsResponse | null;
  subtract: SubtractDetailsResponse | null;
}): JSX.Element => (
  <div className={styles.column}>
    <span className={styles.columnTitle}>Subtract Details</span>
    <div className={styles.fieldsGrid}>
      <CompactField
        label="Subtract Name:"
        value={subtract?.name ?? EMPTY_VALUE}
      />
      <CompactField
        label="Survey Area:"
        value={tract?.referenceLocation?.name ?? EMPTY_VALUE}
      />
      <CompactField
        label="Quarter Call:"
        value={subtract?.legalDescription?.quarterCall ?? EMPTY_VALUE}
      />
      <CompactField
        label="Lots:"
        value={subtract?.legalDescription?.lot ?? EMPTY_VALUE}
      />
      <div className={styles.fieldFullWidth}>
        <CompactField
          label="Full Legal Description:"
          value={
            subtract?.legalDescription?.fullLegalDescription ?? EMPTY_VALUE
          }
          valueLayout="block"
        />
      </div>
      <CompactField
        label="Grid Exception:"
        value={formatBoolean(subtract?.legalDescription?.gridException)}
      />
      <div className={styles.gridSpacer} aria-hidden />
      <CompactField
        label="Subtract Net Acres:"
        value={formatAcres(subtract?.acres)}
      />
      <CompactField
        label="Subtract Gross Acres:"
        value={formatAcres(subtract?.grossAcres)}
      />
      {/* TODO(INS-XXXX): wire once SubtractDetailsResponse exposes
          royalty fields (leaseRoyaltyRate / leaseRoyaltyType /
          royaltyBurdens / orriBurdens). */}
      <CompactField label="Lease Royalty Rate:" value={EMPTY_VALUE} />
      <CompactField label="Lease Royalty Type:" value={EMPTY_VALUE} />
      <CompactField label="Royalty Burdens:" value={EMPTY_VALUE} />
      <CompactField label="ORRI Burdens:" value={EMPTY_VALUE} />
    </div>
  </div>
);

interface EntityCellContentProps {
  insightsEntityDisplay: InsightsEntityDisplay;
  entity: SummaryEntityData['entity'];
  showEntityDetails: boolean;
  isMultipleEntitiesExpanded: boolean;
  onToggleMultipleEntities: () => void;
  onLinkInsightsEntity?: () => void;
  disableLinkInsightsEntity?: boolean;
}

/**
 * Renders the inside of the "Insights Entity" cell, branching on
 * `insightsEntityDisplay.type`. For singleEntity, also surfaces the
 * resolved `entity.address` and `entity.type` underneath.
 *
 * @param {EntityCellContentProps} props - The component props.
 * @return {JSX.Element} The cell content.
 */
const EntityCellContent = ({
  insightsEntityDisplay,
  entity,
  showEntityDetails,
  isMultipleEntitiesExpanded,
  onToggleMultipleEntities,
  onLinkInsightsEntity,
  disableLinkInsightsEntity,
}: EntityCellContentProps): JSX.Element => {
  if (insightsEntityDisplay.type === 'linkButton') {
    return (
      <Button
        kind="primary"
        size="sm"
        renderIcon={Connect}
        onClick={onLinkInsightsEntity}
        disabled={disableLinkInsightsEntity}
      >
        {insightsEntityDisplay.label}
      </Button>
    );
  }
  if (insightsEntityDisplay.type === 'multipleEntities') {
    return (
      <Button
        kind="ghost"
        size="sm"
        renderIcon={isMultipleEntitiesExpanded ? ChevronUp : ChevronDown}
        onClick={onToggleMultipleEntities}
        aria-expanded={isMultipleEntitiesExpanded}
      >
        <FormattedCount value={insightsEntityDisplay.count} /> Insights Entities
      </Button>
    );
  }
  return (
    <div className={styles.entityCell}>
      {renderLink(
        insightsEntityDisplay.entity?.name,
        insightsEntityDisplay.entity?.href
      )}
      {showEntityDetails && (
        <>
          <span className={styles.entityMeta}>
            Address: {entity?.address ?? EMPTY_VALUE}
          </span>
          <span className={styles.entityMeta}>
            Entity Type: {entity?.type ?? EMPTY_VALUE}
          </span>
        </>
      )}
    </div>
  );
};

/**
 * Entity & CRM Account column — Carbon `<Table>` with two cells side
 * by side ("Insights Entity" | "Linked CRM Account") matching the
 * mock's compact horizontal layout. The Insights Entity cell branches
 * on `insightsEntityDisplay`:
 *   - `'linkButton'` → "Link Insights Entity" button
 *   - `'multipleEntities'` → chevron-toggle expand of the entities table below
 *   - `'singleEntity'` → external link + Address + Entity Type stacked
 *
 * @param {object} props - The component props.
 * @return {JSX.Element} The column.
 */
const EntityColumn = ({
  summaryData,
  onLinkInsightsEntity,
  disableLinkInsightsEntity,
}: {
  summaryData: SummaryEntityData;
  onLinkInsightsEntity?: () => void;
  disableLinkInsightsEntity?: boolean;
}): JSX.Element => {
  const {
    entity,
    account,
    entities,
    insightsEntityDisplay,
    showEntityDetails,
    hasMultipleEntities,
  } = summaryData;
  const [isMultipleEntitiesExpanded, setIsMultipleEntitiesExpanded] =
    useState(false);

  return (
    <div className={styles.column}>
      <span className={styles.columnTitle}>Entity & Linked CRM Account</span>
      <Table className={styles.entityTable}>
        <TableHead>
          <TableRow>
            <TableHeader>Insights Entity</TableHeader>
            <TableHeader>Linked CRM Account</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>
              <EntityCellContent
                insightsEntityDisplay={insightsEntityDisplay}
                entity={entity}
                showEntityDetails={showEntityDetails}
                isMultipleEntitiesExpanded={isMultipleEntitiesExpanded}
                onToggleMultipleEntities={() =>
                  setIsMultipleEntitiesExpanded((current) => !current)
                }
                onLinkInsightsEntity={onLinkInsightsEntity}
                disableLinkInsightsEntity={disableLinkInsightsEntity}
              />
            </TableCell>
            <TableCell>
              <div className={styles.entityCell}>
                {renderLink(
                  account?.name,
                  account?.salesforceHref ?? undefined
                )}
                <span className={styles.entityMeta}>
                  Address: {account?.billingAddress ?? EMPTY_VALUE}
                </span>
                {account?.type && (
                  <span className={styles.entityMeta}>
                    Type: {account.type}
                  </span>
                )}
              </div>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {hasMultipleEntities && isMultipleEntitiesExpanded && (
        <Table className={styles.entitiesExpandedTable}>
          <TableHead>
            <TableRow>
              <TableHeader>Insights Entity</TableHeader>
              <TableHeader>Type</TableHeader>
              <TableHeader>Address</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {entities.map((ent) => (
              <TableRow key={ent.id}>
                <TableCell>
                  {renderLink(ent.name, ent.href ?? undefined)}
                </TableCell>
                <TableCell>{ent.type ?? EMPTY_VALUE}</TableCell>
                <TableCell>{ent.address ?? EMPTY_VALUE}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

/**
 * Three-column top section for the Summary step: Tract Details,
 * Subtract Details, and Entity & Linked CRM Account side by side.
 * Pure presentational — caller (T19's `<TractSummary>`) owns the data
 * fetching and passes payloads in. Equal-width grid via SCSS.
 *
 * @param {SummaryTopSectionProps} props - The component props.
 * @return {JSX.Element} The top section grid.
 */
export const SummaryTopSection = ({
  tract,
  subtract,
  summaryData,
  onLinkInsightsEntity,
  disableLinkInsightsEntity,
}: SummaryTopSectionProps): JSX.Element => (
  <div className={styles.grid}>
    <TractDetailsColumn tract={tract} />
    <SubtractDetailsColumn tract={tract} subtract={subtract} />
    <EntityColumn
      summaryData={summaryData}
      onLinkInsightsEntity={onLinkInsightsEntity}
      disableLinkInsightsEntity={disableLinkInsightsEntity}
    />
  </div>
);
